import {useCallback, useEffect, useMemo, useState} from 'react';

import {AuditFilterAction, AuditFilterUser, getFilterOptions} from 'audit/AuditDAO';
import notifications from 'notifications';

import {FILTER_MODE} from './filters/FlexibleDateRangePicker';
import type {DateRange, FilterMode} from './filters/FlexibleDateRangePicker';

interface UseAuditLogFiltersProps {
  ids: Array<string>;
  enabled: boolean;
  resultsWarehouseIds?: Array<string>;
  relatedResultsModelId?: string;
}

interface UseAuditLogFiltersResult {
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  dateMode: FilterMode;
  setDateMode: (mode: FilterMode) => void;
  selectedUserIds: Array<string>;
  setSelectedUserIds: (ids: Array<string>) => void;
  selectedActionValues: Array<string>;
  setSelectedActionValues: (values: Array<string>) => void;
  selectedReasonCodes: Array<string>;
  setSelectedReasonCodes: (values: Array<string>) => void;
  filterUsers: Array<AuditFilterUser>;
  filterActions: Array<AuditFilterAction>;
  filterReasonCodes: Array<string> | null;
  filterOptionsError: boolean;
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
}

export default function useAuditLogFilters({
  ids,
  enabled,
  resultsWarehouseIds,
  relatedResultsModelId,
}: UseAuditLogFiltersProps): UseAuditLogFiltersResult {
  const [dateRange, setDateRange] = useState<DateRange>({startDate: null, endDate: null});
  const [dateMode, setDateMode] = useState<FilterMode>(FILTER_MODE.BETWEEN);
  const [selectedUserIds, setSelectedUserIds] = useState<Array<string>>([]);
  const [selectedActionValues, setSelectedActionValues] = useState<Array<string>>([]);
  const [selectedReasonCodes, setSelectedReasonCodes] = useState<Array<string>>([]);

  const [filterUsers, setFilterUsers] = useState<Array<AuditFilterUser>>([]);
  const [filterActions, setFilterActions] = useState<Array<AuditFilterAction>>([]);
  const [filterReasonCodes, setFilterReasonCodes] = useState<Array<string> | null>(null);
  const [filterOptionsError, setFilterOptionsError] = useState(false);

  const loadFilterOptions = useCallback(async () => {
    try {
      setFilterOptionsError(false);
      const response = await getFilterOptions(ids, {
        resultsWarehouseIds,
        relatedResultsModelId,
      });
      setFilterUsers(response.users);
      setFilterActions(response.actions);
      setFilterReasonCodes(response.reasonCodes);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (err) {
      setFilterOptionsError(true);
      notifications.error('Failed to load filter options. Filters have been disabled.');
    }
  }, [ids, resultsWarehouseIds, relatedResultsModelId]);

  useEffect(() => {
    if (enabled) {
      loadFilterOptions();
    }
  }, [enabled, loadFilterOptions]);

  const hasActiveFilters = useMemo(
    () =>
      dateRange.startDate != null ||
      dateRange.endDate != null ||
      selectedUserIds.length > 0 ||
      selectedActionValues.length > 0 ||
      selectedReasonCodes.length > 0,
    [dateRange, selectedUserIds, selectedActionValues, selectedReasonCodes]
  );

  const clearAllFilters = useCallback(() => {
    setDateRange({startDate: null, endDate: null});
    setDateMode(FILTER_MODE.BETWEEN);
    setSelectedUserIds([]);
    setSelectedActionValues([]);
    setSelectedReasonCodes([]);
  }, []);

  return {
    dateRange,
    setDateRange,
    dateMode,
    setDateMode,
    selectedUserIds,
    setSelectedUserIds,
    selectedActionValues,
    setSelectedActionValues,
    selectedReasonCodes,
    setSelectedReasonCodes,
    filterUsers,
    filterActions,
    filterReasonCodes,
    filterOptionsError,
    hasActiveFilters,
    clearAllFilters,
  };
}
